import { Menubar } from "@base-ui/react/menubar";
import { Menu } from "@base-ui/react/menu";
import { Check, ChevronRight, Settings } from "lucide-react";
import { isPathActive, type ShellIcon } from "./types";

export interface MenubarDestination {
    label: string;
    path: string;
    icon: ShellIcon;
    count?: number;
}

/** A nested menu of destinations that opens to the side (shadcn's MenubarSub). */
export interface MenubarSubmenu {
    label: string;
    icon: ShellIcon;
    destinations: readonly MenubarDestination[];
}

/** A run of destinations below a separator, with an optional label, then any submenus. */
export interface MenubarSection {
    label?: string;
    destinations: readonly MenubarDestination[];
    submenus?: readonly MenubarSubmenu[];
}

export interface MenubarGroup {
    label: string;
    destinations: readonly MenubarDestination[];
    /** Further sections, each below a separator. */
    sections?: readonly MenubarSection[];
    /** Badge on the group trigger (e.g. unread mail). */
    count?: number;
    /** Optional trailing "Settings" entry below a separator. */
    settings?: { path: string; label?: string };
}

const everyDestination = (group: MenubarGroup) => [
    ...group.destinations,
    ...(group.sections ?? []).flatMap(section => [...section.destinations, ...(section.submenus ?? []).flatMap(menu => menu.destinations)]),
];

/**
 * Desktop application menubar. Base UI's Menubar coordinates focus, arrow-key
 * navigation, sibling-menu switching, submenus, and menu dismissal. Navigation
 * is delegated to the host so the shell stays router-agnostic.
 */
export function WorkspaceMenubar({
    groups,
    pathname,
    onNavigate,
    ariaLabel = "Application",
}: {
    groups: readonly MenubarGroup[];
    pathname: string;
    onNavigate: (path: string) => void;
    ariaLabel?: string;
}) {
    const items = (destinations: readonly MenubarDestination[]) => destinations.map(item => {
        const Icon = item.icon;
        const selected = isPathActive(pathname, item.path);
        return (
            <Menu.Item className="workspace-menu-item" key={item.path} onClick={() => onNavigate(item.path)}>
                <Icon className="workspace-menu-icon" aria-hidden="true" />
                <span>{item.label}</span>
                {item.count != null && item.count > 0 && <small className="workspace-menu-item-count">{item.count}</small>}
                {selected && <Check className="workspace-menu-check" aria-label="Current view" />}
            </Menu.Item>
        );
    });
    return (
        <Menubar className="workspace-menubar" aria-label={ariaLabel}>
            {groups.map(group => {
                const active = everyDestination(group).some(item => isPathActive(pathname, item.path));
                return (
                    <Menu.Root key={group.label}>
                        <Menu.Trigger className="workspace-menu-trigger" data-active={active || undefined}>
                            {group.label}{group.count != null && group.count > 0 && <span className="workspace-menu-count">{group.count > 99 ? "99+" : group.count}</span>}
                        </Menu.Trigger>
                        <Menu.Portal>
                            <Menu.Positioner sideOffset={4} align="start" className="workspace-menu-positioner">
                                <Menu.Popup className="workspace-menu-content">
                                    <Menu.Group>
                                        <Menu.GroupLabel className="workspace-menu-label">{group.label}</Menu.GroupLabel>
                                        {items(group.destinations)}
                                    </Menu.Group>
                                    {group.sections?.map((section, index) => (
                                        <Menu.Group key={section.label ?? index}>
                                            <Menu.Separator className="workspace-menu-separator" />
                                            {section.label && <Menu.GroupLabel className="workspace-menu-label">{section.label}</Menu.GroupLabel>}
                                            {items(section.destinations)}
                                            {section.submenus?.map(menu => {
                                                const Icon = menu.icon;
                                                const inside = menu.destinations.some(item => isPathActive(pathname, item.path));
                                                return (
                                                    <Menu.SubmenuRoot key={menu.label}>
                                                        <Menu.SubmenuTrigger className="workspace-menu-item" data-active={inside || undefined}>
                                                            <Icon className="workspace-menu-icon" aria-hidden="true" />
                                                            <span>{menu.label}</span>
                                                            <ChevronRight className="workspace-menu-chevron" aria-hidden="true" />
                                                        </Menu.SubmenuTrigger>
                                                        <Menu.Portal>
                                                            <Menu.Positioner side="right" align="start" alignOffset={-5} className="workspace-menu-positioner">
                                                                <Menu.Popup className="workspace-menu-content">{items(menu.destinations)}</Menu.Popup>
                                                            </Menu.Positioner>
                                                        </Menu.Portal>
                                                    </Menu.SubmenuRoot>
                                                );
                                            })}
                                        </Menu.Group>
                                    ))}
                                    {group.settings && <>
                                        <Menu.Separator className="workspace-menu-separator" />
                                        <Menu.Item className="workspace-menu-item" onClick={() => onNavigate(group.settings!.path)}>
                                            <Settings className="workspace-menu-icon" aria-hidden="true" />{group.settings.label ?? "Settings"}
                                        </Menu.Item>
                                    </>}
                                </Menu.Popup>
                            </Menu.Positioner>
                        </Menu.Portal>
                    </Menu.Root>
                );
            })}
        </Menubar>
    );
}
